import { useState, useContext, useEffect } from "react";
import axios from "axios";
import Alert from "../Alert";
import { alertContext } from "../contexts/AlertProvider";
import PermissionGate from "../layout/PermissionGate";

const Bookeey = () => {
  const [data, setData] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [_, setAlertConfig] = useContext(alertContext);
  const token = localStorage.getItem("token");

  const fetchSetting = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}payment-gateway/bookeey/integration/get`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.code === 1) {
        setData({
          merchantId: res?.data?.data?.merchantId ?? "",
          secretKey: res?.data?.data?.secretKey ?? "",
          paymentMethods: Array.isArray(res?.data?.data?.paymentMethods)
            ? res?.data?.data?.paymentMethods
            : [],
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPaymentMethods = () => {
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT}payment-gateway/bookeey/integration/payment-methods`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          setPaymentMethods(res.data.data);
        }
      })
      .catch(console.error);
  };

  const createIntegration = async () => {
    if (!data?.merchantId?.trim()) {
      setError((prev) => ({ ...prev, merchantId: "Please enter merchant id" }));
      return;
    }
    if (!data?.secretKey?.trim()) {
      setError((prev) => ({ ...prev, secretKey: "Please enter secret key" }));
      return;
    }

    setError({});
    setIsUpdating(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}payment-gateway/bookeey/integration/create`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setAlertConfig({
        show: true,
        message: res.data.message,
        type: res.data.code === 1 ? "success" : "danger",
      });
      reset();
    } catch (err) {
      console.error(err);
      setAlertConfig({
        show: true,
        message: "Something went wrong",
        type: "danger",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const reset = () => {
    setError({});
  };

  useEffect(() => {
    fetchSetting();
    fetchPaymentMethods();
  }, []);

  return (
    <form className="mt-4" autoComplete="off" id="bookeeyIntegrationForm">
      <Alert />
      {/* Start:Integration */}
      <div className="row g-5 g-lg-10 mb-5">
        {/*begin::Card*/}
        <PermissionGate required={`view_bookeey_integration`}>
          <div className="card">
            {/*begin::Card header*/}
            <div className="card-header">
              {/*begin::Card title*/}
              <div className="card-title">Bookeey Integration</div>
              {/*begin::Card title*/}
            </div>
            {/*end::Card header*/}
            {/*begin::Card body*/}
            <div className="card-body py-4">
              <div className="row">
                <div className="col-md-6 mb-3 fv-row">
                  <label className="form-label required">Merchant Id</label>
                  <input
                    type="text"
                    className="form-control add-driver-field"
                    placeholder="Enter merchant id"
                    value={data?.merchantId ?? ""}
                    disabled={isLoading || isUpdating}
                    onChange={(e) => {
                      setData((prev) => ({
                        ...prev,
                        merchantId: e.target.value,
                      }));
                      setError((prev) => ({ ...prev, merchantId: "" }));
                    }}
                  />
                  <small className="text-danger mt-2">
                    {error?.merchantId}
                  </small>
                </div>
                <div className="col-md-6 mb-3 fv-row">
                  <label className="form-label required">Secret Key</label>
                  <input
                    type="text"
                    className="form-control add-driver-field"
                    placeholder="Enter secret key"
                    value={data?.secretKey ?? ""}
                    disabled={isLoading || isUpdating}
                    onChange={(e) => {
                      setData((prev) => ({
                        ...prev,
                        secretKey: e.target.value,
                      }));
                      setError((prev) => ({ ...prev, secretKey: "" }));
                    }}
                  />
                  <small className="text-danger mt-2">{error?.secretKey}</small>
                </div>
                <div className="col-md-12 mb-3">
                  <h6 className="text-gray-800 my-3">Payment Methods</h6>
                  <div className="form-check form-check-sm">
                    <input
                      value="all"
                      checked={
                        paymentMethods?.every((method) =>
                          data?.paymentMethods?.includes(method),
                        ) ?? false
                      }
                      onChange={(e) => {
                        if (e.target.checked === true) {
                          setData((prev) => ({
                            ...prev,
                            paymentMethods: Array.isArray(paymentMethods)
                              ? paymentMethods
                              : [],
                          }));
                        } else {
                          setData((prev) => ({
                            ...prev,
                            paymentMethods: [],
                          }));
                        }
                      }}
                      className="form-check-input me-3"
                      type="checkbox"
                    />
                    <label className="form-check-label">
                      <div className="fw-bold text-gray-800">Enable All</div>
                    </label>
                  </div>
                </div>
                <div className="col-md-12 mb-3">
                  <div className="row">
                    {Array.isArray(paymentMethods) &&
                      paymentMethods?.map((method, index) => (
                        <div className="col-md-auto mb-3" key={index}>
                          <div className="form-check form-check-sm">
                            <input
                              value={method}
                              checked={
                                data?.paymentMethods?.includes(method) ?? false
                              }
                              onChange={(e) => {
                                const checked = e.target.checked;

                                setData((prev) => {
                                  const paymentMethods =
                                    prev?.paymentMethods || [];

                                  return {
                                    ...prev,
                                    paymentMethods: checked
                                      ? [
                                          ...new Set([
                                            ...paymentMethods,
                                            method,
                                          ]),
                                        ]
                                      : paymentMethods.filter(
                                          (item) => item !== method,
                                        ),
                                  };
                                });
                              }}
                              className="form-check-input me-3"
                              type="checkbox"
                            />
                            <label className="form-check-label">
                              <div className="fw-bold text-gray-800">
                                {method}
                              </div>
                            </label>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            {/*end::Card body*/}
            <PermissionGate
              required={`create_bookeey_integration`}
              fallback={<></>}
            >
              <div className="card-footer d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createIntegration}
                  disabled={isLoading || isUpdating}
                  style={{ minWidth: "160px" }}
                >
                  {isLoading || isUpdating ? (
                    <>
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </>
                  ) : (
                    "Update"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={reset}
                  disabled={isLoading || isUpdating}
                  style={{ minWidth: "160px" }}
                >
                  Reset
                </button>
              </div>
            </PermissionGate>
          </div>
        </PermissionGate>
        {/*end::Card*/}
      </div>
      {/* End:Integration */}
    </form>
  );
};

export default Bookeey;
